import * as R from 'ramda'
import {cardsPerSet} from '@kapitalist/contract/ruleset'
import type {
  TAcceptForcedDealDestination,
  TMinorActionPromptDataForcedDeal,
} from '@kapitalist/contract/types'
import {mkPortfolioNeighborhoodId} from '@kapitalist/contract/types'
import * as S from '@kapitalist/utils/Set'
import {skins} from '~/game/skins'
import {BY_ITSELF_PORTFOLIO_NEIGHBORHOOD_ID} from '~/game/types/neighborhoodId'
import Card from '~/models/game/Card'
import PropertyWildCard from '~/models/game/Cards/PropertyWildCard'
import {propertyCardFactory} from '~/models/game/factories'
import type GamePlayer from '~/models/game/GamePlayer'
import {PlayerGamePosition} from '~/models/game/GamePosition'
import type {
  TMinorActionPromptUserFacingDescriptionData,
  TMinorActionPromptValidResponseData,
} from '~/models/game/MinorActionPrompt'
import MinorActionPrompt from '~/models/game/MinorActionPrompt'
import MinorActionAcceptForcedDeal from '~/models/game/MinorActions/MinorActionAcceptForcedDeal'
import type PortfolioNeighborhood from '~/models/game/PortfolioNeighborhood'
import StandardPortfolioNeighborhood from '~/models/game/PortfolioNeighborhoods/StandardPortfolioNeighborhood'
import WildCardPortfolioNeighborhood from '~/models/game/PortfolioNeighborhoods/WildCardPortfolioNeighborhood'
import {
  switchOnPropertyCard,
  type AnyMinorAction,
  type AnyPropertyCard,
} from '~/models/game/types'
import {mutateIf} from '~/utils/etc'
import {sentenceCase, xVerbY} from '~/utils/language'

export default class MinorActionPromptForcedDeal extends MinorActionPrompt<TMinorActionPromptDataForcedDeal> {
  requestedCard: AnyPropertyCard
  offeredCard: AnyPropertyCard

  constructor(
    data: TMinorActionPromptDataForcedDeal,
    majorActor: GamePlayer,
    minorActor: GamePlayer
  ) {
    super(data, majorActor, minorActor)
    this.requestedCard = propertyCardFactory(data.requestedCard)
    this.offeredCard = propertyCardFactory(data.offeredCard)
  }

  protected _validResponses(
    data: TMinorActionPromptValidResponseData
  ): AnyMinorAction | AnyMinorAction[] {
    return this.withJustSayNo(
      data,
      new MinorActionAcceptForcedDeal({
        minorActorPlayerId: this.minorActorPlayerId,
        type: 'forcedDeal',
        offeredCard: this.offeredCard.datasource,
        requestedCard: this.requestedCard.datasource,
        destination: UNSET_FORCED_DEAL_DESTINATION, // the UI overwrites this before submit
      })
    )
  }

  public userFacingDescription(
    data: TMinorActionPromptUserFacingDescriptionData
  ): string {
    const {gamePosition, tense, skin} = data
    const currentPlayer =
      gamePosition instanceof PlayerGamePosition
        ? gamePosition.currentPlayer
        : undefined
    const {majorActor, minorActor} = this

    const {conjugatedVerb, xNoun, yNounPossessive} = xVerbY(
      tense,
      minorActor,
      'respondTo',
      majorActor,
      currentPlayer?.id
    )

    const dropFirstNoun = tense === 'imperativePrompt'
    return skins[skin].gameLogDescriptions.minorActionPrompts.forcedDeal({
      tense,
      actor: dropFirstNoun
        ? undefined
        : xNoun === minorActor.username
          ? xNoun
          : sentenceCase(xNoun),
      verb: mutateIf(conjugatedVerb, dropFirstNoun, sentenceCase),
      targetPossessive: yNounPossessive,
      cardName: Card.displayNameForCard('forcedDeal', skin),
    })
  }
}

// Where a force-dealt card may land on the targeted player's board. Shared with the JSN
// battle flow, where the same acceptance decision resurfaces via a justSayNo prompt.
export const possibleDestinationNeighborhoodsForForcedDeal = (data: {
  offeredCard: AnyPropertyCard
  requestedCard: AnyPropertyCard
  targetedPlayer: GamePlayer
}): PortfolioNeighborhood[] => {
  const {offeredCard, requestedCard, targetedPlayer} = data

  const stolenFromNeighborhood = targetedPlayer.portfolio.neighborhoods.find(pn =>
    pn.properties.find(c => c.id === requestedCard.id)
  )

  if (!stolenFromNeighborhood) {
    throw new Error(
      `couldn't find target neighborhood containing the requestedCard of a forced deal`
    )
  }

  // a forced deal removes a card from a neighborhood, and gives us a card.
  // the given card can go into:
  //
  // 1. if it has room, a neighborhood of the same type
  //    - note that if e.g. a solo green/darkBlue is taken and replaced by a green, we first exclude the green/dkBlue
  //      neighborhood that will disappear, and then (3) below will add a new BY_ITSELF... option for it
  // 2. if it has room*, an alone neighborhood (it can join a PWC)
  //    * this gets tricky if the alone neighborhood has 2 PWCs in it, because in that scenario a 2-cards-per-set
  //      colored card (darkBlue/brown/utility) can't join the PWCs! Do you let them move a PWC to join the
  //      new brown/utility/darkBlue? Probably not, kinda complex. We don't allow it here for now.
  // 3. a (new) neighborhood of its own,
  //   - for standard properties, iff there are no incompleteSets of its type available
  //   - for dual properties, iff either/both of its colors aren't in incompleteSets
  //   - for PWCs, if there are no incompleteSets at all

  const targetNeighborhoods = targetedPlayer.portfolio
    .incompleteSets()
    .filter(pn => {
      if (
        pn.id === stolenFromNeighborhood.id &&
        stolenFromNeighborhood.properties.length === 1
      ) {
        // this neighborhood is about to be gone, so we can't add the offeredCard to it - (1)'s note above
        return false
      }

      // case 2
      if (pn.neighborhood === 'alone') {
        return (
          offeredCard instanceof PropertyWildCard ||
          // only if there is room for the card to join the PWCs (for 2-card-sets as noted above)
          S.anyPass(
            cardType => pn.properties.length < cardsPerSet[cardType],
            offeredCard.cardTypes()
          )
        )
      }

      // else, case 1
      return offeredCard.cardTypes().has(pn.neighborhood)
    })

  // case 3, a (new) neighborhood of its own:
  switchOnPropertyCard<void>({
    //   - for standard properties, iff there are no incompleteSets of its type available
    whenStandard: spc => {
      if (R.none(pn => spc.type === pn.neighborhood, targetNeighborhoods)) {
        targetNeighborhoods.push(
          new StandardPortfolioNeighborhood({
            id: BY_ITSELF_PORTFOLIO_NEIGHBORHOOD_ID,
            neighborhood: spc.type,
            properties: [],
            residences: [],
          })
        )
      }
    },
    //   - for dual properties, iff either/both of its colors aren't in incompleteSets
    whenDual: dpc => {
      // which of the Dual neighborhoods are missing in targetNeighborhoods?
      S.filter(
        dpcType =>
          targetNeighborhoods.filter(pn => pn.neighborhood === dpcType).length === 0,
        dpc.standardPropertyTypesSet()
      ).forEach(missingNeighborhood =>
        targetNeighborhoods.push(
          new StandardPortfolioNeighborhood({
            id: BY_ITSELF_PORTFOLIO_NEIGHBORHOOD_ID,
            neighborhood: missingNeighborhood,
            properties: [],
            residences: [],
          })
        )
      )
    },
    //   - for PWCs, if there are no incompleteSets at all
    whenWild: () => {
      if (targetNeighborhoods.length === 0) {
        targetNeighborhoods.push(
          new WildCardPortfolioNeighborhood({
            id: BY_ITSELF_PORTFOLIO_NEIGHBORHOOD_ID,
            neighborhood: 'alone',
            properties: [],
          })
        )
      }
    },
  })(offeredCard)

  return targetNeighborhoods
}

// Placeholder on a freshly-built accept action; a destination picker overwrites it before submit.
export const UNSET_FORCED_DEAL_DESTINATION: TAcceptForcedDealDestination = {
  kind: 'existingNeighborhood',
  portfolioNeighborhoodId: mkPortfolioNeighborhoodId('unset_pnId'),
}

// Maps a chosen destination neighborhood (one of `possibleDestinationNeighborhoodsForForcedDeal`,
// whose "move to a new set" entries have the `BY_ITSELF...` id) to the contract destination.
export const neighborhoodToAcceptForcedDealDestination = (
  nb: PortfolioNeighborhood
): TAcceptForcedDealDestination =>
  nb.id !== BY_ITSELF_PORTFOLIO_NEIGHBORHOOD_ID
    ? {kind: 'existingNeighborhood', portfolioNeighborhoodId: nb.id}
    : nb.neighborhood === 'alone'
      ? {kind: 'newWildNeighborhood'}
      : {kind: 'asColor', color: nb.neighborhood}
